import React, { useEffect, useRef, useState } from 'react'
import Sheet from '@amzn/meridian/sheet'
import SearchField from '@amzn/meridian/search-field'
import Button from '@amzn/meridian/button'
import Checkbox from '@amzn/meridian/checkbox'
import Icon from '@amzn/meridian/icon'
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small'
import Heading from '@amzn/meridian/heading'
import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Box from '@amzn/meridian/box'
import Divider from '@amzn/meridian/divider'
import Text from '@amzn/meridian/text'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { IStoreFilterOption } from '../types'
import { APPLY_FILTERS, CLOSE, FILTER_HEADING, NO_STORES_FOUND, SEARCH_STORES_PLACEHOLDER } from '../constants'
import './StoreFilterSheet.scss'

interface IStoreFilterSheetProps {
    open: boolean
    onClose: () => void
    /** Stores present in the unread "All stores" feed. */
    stores: IStoreFilterOption[]
    /** Store ids already applied; the sheet re-seeds from these each time it opens. */
    initialSelectedStoreIds: string[]
    /** Called with the chosen store ids on Apply. An empty array clears the filter. */
    onApply: (selectedStoreIds: string[]) => void
}

/**
 * Bottom-sheet multi-select store filter for the Action Center "All stores" tab. Mirrors the
 * legacy NotificationsPage filter sheet (search + Apply) but is self-contained to v2 and keeps
 * its working selection local — a dismiss without Apply discards it.
 */
export const StoreFilterSheet: React.FC<IStoreFilterSheetProps> = ({
    open,
    onClose,
    stores,
    initialSelectedStoreIds,
    onApply,
}) => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const [tempSelectedStoreIds, setTempSelectedStoreIds] = useState<string[]>(initialSelectedStoreIds)
    const [searchQuery, setSearchQuery] = useState('')

    // Re-seed local state only on the closed->open transition. initialSelectedStoreIds is a
    // memoized array that gets a NEW reference whenever the redux store list refreshes, so keying
    // the re-seed off its identity would wipe an in-progress selection/search mid-interaction if
    // the list refreshed while the sheet is open.
    const prevOpenRef = useRef(false)
    useEffect(() => {
        if (open && !prevOpenRef.current) {
            setTempSelectedStoreIds(initialSelectedStoreIds)
            setSearchQuery('')
        }
        prevOpenRef.current = open
    }, [open, initialSelectedStoreIds])

    const query = searchQuery.trim().toLowerCase()
    const filteredStores = query === ''
        ? stores
        : stores.filter((s) =>
            s.storeName.toLowerCase().includes(query) || s.storeIds.some((id) => id.toLowerCase().includes(query)))

    const handleToggle = (storeId: string) => {
        setTempSelectedStoreIds((prev) =>
            prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId])
    }

    const handleApply = () => {
        onApply(tempSelectedStoreIds)
        onClose()
    }

    const handleSearchSubmit = () => {
        (document.activeElement as HTMLElement)?.blur()
    }

    // Shown while anything can be applied — including when the user deselects everything, so a
    // live filter can be cleared from here as well as from the page's Clear control.
    const showApply = tempSelectedStoreIds.length > 0 || initialSelectedStoreIds.length > 0

    return (
        <Sheet
            type="overlay"
            side="bottom"
            open={open}
            onClose={onClose}
            spacingInset="none"
            className="action-center-store-filter-sheet"
            data-atb-id="action-center-store-filter-sheet"
            aria-labelledby="ac-store-filter-heading"
        >
            <div className="ac-store-filter-sheet-content">
                <Column spacing="none" height="100%">
                    <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="400 400 none 400">
                        <Heading level={5} id="ac-store-filter-heading">{t(bundle, FILTER_HEADING)}</Heading>
                        <Button
                            type="icon"
                            size="small"
                            onClick={onClose}
                            data-testid="close-store-filter-sheet"
                        >
                            <Icon tokens={closeSmallTokens}>{t(bundle, CLOSE)}</Icon>
                        </Button>
                    </Row>
                    <Column spacingInset="300 400 400 400">
                        <Box type="fill" spacingInset="300 400">
                            <SearchField
                                value={searchQuery}
                                onChange={setSearchQuery}
                                onSubmit={handleSearchSubmit}
                                onClear={() => setSearchQuery('')}
                                placeholder={t(bundle, SEARCH_STORES_PLACEHOLDER)}
                                searchButton={true}
                            />
                        </Box>
                    </Column>
                    {filteredStores.length === 0 ? (
                        <Column alignmentHorizontal="center" spacingInset="600 400" data-testid="store-filter-empty">
                            <Text color="secondary">{t(bundle, NO_STORES_FOUND)}</Text>
                        </Column>
                    ) : (
                        <div className="ac-store-filter-sheet-list">
                            {filteredStores.map((store) => (
                                <React.Fragment key={store.storeId}>
                                    <div className="ac-store-filter-row" data-testid={`store-filter-item-${store.storeId}`}>
                                        <Checkbox
                                            checked={tempSelectedStoreIds.includes(store.storeId)}
                                            onChange={() => handleToggle(store.storeId)}
                                            data-testid={`toggle-${store.storeId}`}
                                        >
                                            {store.storeName}
                                        </Checkbox>
                                    </div>
                                    <Divider size="small" />
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                    {showApply && (
                        <div className="ac-apply-filters-container primary-theme-button">
                            <Button onClick={handleApply} data-testid="apply-filters-button">
                                {t(bundle, APPLY_FILTERS, { filterCount: tempSelectedStoreIds.length })}
                            </Button>
                        </div>
                    )}
                </Column>
            </div>
        </Sheet>
    )
}

export default StoreFilterSheet
